import React, { useState, useCallback } from "react";
import { useQueryClient, useSuspenseQuery, useQuery } from "@tanstack/react-query";
import { withFallback } from "vike-react-query";
import { usePageContext } from "vike-react/usePageContext";
import { useSelector, useDispatch } from "react-redux";
import { Rating } from "@mui/material";
import { fetchBook, fetchGoodreadsData } from "#redux/slices/bookSlice";
import { setCurrentUserBook } from "#redux/slices/userBookSlice";
import { openModal } from "#redux/slices/globalUISlice";
import BookStatusButton from "#helpers/BookStatusButton";
import ReadingInstances from "#helpers/ReadingInstances";
import Button from "#helpers/Button";
import BookCover from "#helpers/BookCover";
import HeadTags from "#helpers/HeadTags";
import Forum from "#components/forum/Forum";
import ForumSearch from "#components/forum/ForumSearch";
import BookReviews from "./BookReviews";
import BookRecommendations from "./BookRecommendations";
import BookDetails from "./BookDetails";
import { getMaterialIcon } from "#utils/icons";
const StarRoundedIcon = getMaterialIcon("StarRounded");
import LoginPrompt from "#helpers/LoginPrompt";
import Loader from "#helpers/Loader";

const BookPage = () => {
  const [bookDataLoading, setBookDataLoading] = useState(false);
  const {
    routeParams: { uuid },
    urlParsed: {
      search: { view = "main" },
    },
  } = usePageContext();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { currentUserId } = useSelector((state) => state.user);
  const [searchQuery, setSearchQuery] = useState("");

  const bookQuery = useQuery({
    queryKey: ["book", uuid],
    queryFn: () => dispatch(fetchBook(uuid)).unwrap(),
    enabled: !!currentUserId,
    suspense: false,
  });

  const currentBook = bookQuery.data?.book;
  const currentUserBook = bookQuery.data?.user_book;

  const handleFetchGoodreadsData = () => {
    setBookDataLoading(true);
    dispatch(fetchGoodreadsData(currentBook.uuid)).then(() => {
      bookQuery.refetch();
      setBookDataLoading(false);
    });
  };

  const clearSearch = useCallback(() => {
    setSearchQuery("");
  }, []);

  const userBookStatus = currentUserBook?.status || "none";
  const canWriteReview =
    currentUserBook?.has_ever_finished || userBookStatus === "dnf" || userBookStatus === "finished";

  const setUserBookData = (userBook) => {
    dispatch(setCurrentUserBook(userBook));
    queryClient.setQueryData(["book", uuid], (oldData) => ({ book: oldData.book, user_book: userBook }));
  };

  if (bookQuery.isLoading) return <Loader />;

  return (
    <div className="w-full relative flex flex-col box-border lg:flex-row gap-4 lg:gap-16 mb-[100px]">
      {currentUserId ? (
        <>
          <HeadTags title={currentBook?.title} description={currentBook?.description} image={currentBook?.image_url} />
          <div className="flex px-4 pb-4 pt-8 lg:p-0 border-b bg-gradient-to-r from-pastelBlue to-pastelPink lg:border-none w-[100vw] lg:flex-col lg:fixed lg:top-[65px] lg:left-0 lg:pt-[80px] lg:h-[100vh] lg:w-[305px] lg:bg-bookpage-squiggle lg:bg-cover flex-none gap-4 lg:gap-8 justify-start items-start lg:items-center z-10">
            <div className="flex flex-col gap-4 items-center">
              <div className="w-[110px] h-[170px] lg:w-[207px] lg:h-80 flex-none">
                <BookCover book={currentBook} clickable={true} size="w-[110px] h-[170px] lg:w-[207px] lg:h-80" />
              </div>
              {!currentBook.image_url && !currentBook.description && currentBook.goodreads_id && (
                <Button
                  text="Fetch Bookcover"
                  onClick={handleFetchGoodreadsData}
                  color="bg-orange"
                  disabled={bookDataLoading}
                />
              )}
            </div>
            <div className="lg:hidden flex flex-col gap-1 justify-center text-left">
              <BookDetails book={currentBook} />
              <div className="lg:hidden flex flex-col gap-2">
                <BookStatusButton
                  userBook={currentUserBook}
                  bookId={currentBook.id}
                  variant="button"
                  setUserBookData={setUserBookData}
                />
                {currentUserBook && <ReadingInstances userBook={currentUserBook} setUserBookData={setUserBookData} />}
              </div>
            </div>
            <div className="hidden lg:flex flex-col gap-6 items-center">
              <BookStatusButton
                userBook={currentUserBook}
                bookId={currentBook.id}
                variant="button"
                setUserBookData={setUserBookData}
              />
              {currentUserBook && <ReadingInstances userBook={currentUserBook} setUserBookData={setUserBookData} />}
              <div className="flex flex-col items-center gap-2">
                <p className={`text-sm mr-1 font-space font-bold ${!canWriteReview && "text-gray"}`}>Your rating:</p>
                <button
                  onClick={() => {
                    if (!currentUserId) return dispatch(openModal("loginPrompt"));
                    dispatch(
                      openModal({
                        name: "newReview",
                        data: {
                          userBook: { ...currentUserBook, review: currentUserBook?.review },
                          review: currentUserBook?.review,
                          handleReviewSubmit: bookQuery.refetch,
                        },
                      })
                    );
                  }}
                  className="flex flex-col w-[106px] items-center "
                >
                  <Rating
                    name="rating"
                    readOnly
                    value={Number(currentUserBook?.review?.overall_rating ?? 0)}
                    precision={0.5}
                    size="medium"
                    icon={<StarRoundedIcon className="stroke-offBlack text-yellow" fontSize="medium" />}
                    emptyIcon={
                      <StarRoundedIcon
                        className={`${canWriteReview ? "stroke-black text-white" : "stroke-gray text-transparent"}`}
                        fontSize="medium"
                      />
                    }
                  />
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-col p-4 grow lg:ml-[335px] lg:mt-10 w-full max-w-[800px] justify-center lg:justify-start text-left z-0">
            {view == "main" && <BookRecommendations uuid={uuid} refreshData={bookQuery.refetch} />}
            {view == "forum" && (
              <div className="flex flex-col w-full max-w-[800px]">
                <a href={`/books/${uuid}`} className="text-[#004ABE] font-bold text-sm mb-3">
                  ⬅ Back to book
                </a>
                <div className="flex justify-center w-full flex-col items-center gap-4 mt-4">
                  <ForumSearch forum={currentBook} setSearchQuery={setSearchQuery} onClearQuery={clearSearch} />
                  <Forum resource={currentBook} resourceType="Book" searchQuery={searchQuery} />
                </div>
              </div>
            )}
            {view == "reviews" && (
              <div className="flex flex-col w-full max-w-[800px]">
                <a href={`/books/${uuid}`} className="text-[#004ABE] font-bold text-sm mb-3">
                  ⬅ Back to book
                </a>
                <BookReviews uuid={uuid} userBookStatus={userBookStatus} />
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="flex items-center justify-center flex-col w-full max-w-[800px] mt-[50px]">
          <LoginPrompt />
        </div>
      )}
    </div>
  );
};

export default withFallback(BookPage, undefined, () => <h2>Oops! We couldn't find that.</h2>);
